import React, { Suspense, useContext } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import SignIn from 'containers/auth/signIn';
import ForgotPassword from 'containers/auth/forgotPassword';
import ResetPassword from 'containers/auth/resetPassword';
import { routes } from './routes';
import { UserContext } from 'providers/userProvider';
import NotificationProvider from 'providers/notificationsProvider';
import { LayoutProvider } from '../providers/layout/layoutProvider';
import withPrivateTrainersRoute from '../hocs/withPrivateTrainersRoute';
import { OnboardingProvider } from 'providers/OnboardingProvider';
import { InputFocusProvider } from '../providers/InputFocusProvider';
import { NEW_BASE } from 'new/paths';
import { ForgotRoute, ResetRoute, SignInRoute, SignInStepRoute } from 'new/signin/SignInRoutes';
import Loader from 'components/loader/Loader';
import { lazyNamed, lazyPage } from 'utils/lazyPage';

// Each portal downloads only when it is opened: the current portal never loads
// the new one's code, and the new one never loads the current one's pages.
const TrainerRouter = lazyPage(() => import('containers/app/trainer/Router'));
const NewPortal = lazyNamed(() => import('new/NewPortal'), 'NewPortal');
const FirstSignIn = lazyPage(() => import('containers/auth/firstSignIn'));

// Made once, not on every render: each is the same component every time.
const PrivateNewPortal = withPrivateTrainersRoute(NewPortal);
const PrivateTrainerRouter = withPrivateTrainersRoute(TrainerRouter);
const PrivateFirstSignIn = withPrivateTrainersRoute(FirstSignIn);

const MainRouter = () => {
  const { user } = useContext(UserContext);

  return (
    <LayoutProvider>
      <NotificationProvider>
        <OnboardingProvider>
          <InputFocusProvider>
            <Suspense fallback={<Loader />}>
            <Switch>
              {/* Sign-in pages: the new screens while the "new-sign-in" switch is on, the current ones otherwise. */}
              <Route
                exact
                path={routes.SIGN_IN}
                render={(props) => <SignInRoute classic={SignIn} route={props} />}
              />
              <Route
                exact
                path={routes.FORGOT_PASSWORD}
                render={(props) => <ForgotRoute classic={ForgotPassword} route={props} />}
              />
              <Route
                path={routes.RESET_PASSWORD}
                render={(props) => <ResetRoute classic={ResetPassword} route={props} />}
              />
              <Route path={routes.SIGN_IN_STEP} component={SignInStepRoute} />
              {/* The new StrengthX (docs/redesign), for flagged accounts; it sends anyone else to the current portal. */}
              <Route
                path={NEW_BASE}
                render={(props) =>
                  <PrivateNewPortal user={user} {...props} />
                }
              />
              <Route
                path={routes.MAIN}
                render={(props) =>
                  <PrivateTrainerRouter user={user} {...props} />
                }
              />
              <Route
                exact
                path={routes.FIRST_LOG_IN}
                render={(props) =>
                  <PrivateFirstSignIn user={user} {...props} />
                }
              />
              <Route
                render={() => {
                  return <Redirect to="/trainer/trainers-clients" />;
                }}
              />
            </Switch>
            </Suspense>
          </InputFocusProvider>
        </OnboardingProvider>
      </NotificationProvider>
    </LayoutProvider>
  );
};

export default MainRouter;
